/** Galerie fișiere (App-Galerie.dc.html) — drag&drop, badge „folosit în N clipuri", filtre, ștergere. */
import { useEffect, useRef, useState } from 'react';
import { useFiles, useMutation, useInvalidate, qk } from '../api/hooks';
import { deleteFile } from '../api/endpoints';
import { uploadFile } from '../lib/upload';
import { humanError, ApiError } from '../api/client';
import { useToast } from '../context/ToastContext';
import { MediaImage } from '../components/Media';
import { EmptyState, PageHeader, Segmented, Skeleton } from '../components/ui';
import { formatNumber } from '../lib/format';

type Filter = 'toate' | 'image' | 'video' | 'audio';

export function GaleriePage() {
  const files = useFiles();
  const invalidate = useInvalidate();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState<Filter>('toate');
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);

  useEffect(() => {
    document.title = 'Galerie fișiere — Reclame Video';
  }, []);

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteFile(id),
    onSuccess: () => {
      invalidate([qk.files()]);
      toast.push({ kind: 'info', title: 'Fișier șters' });
    },
    onError: (err) => {
      const msg = err instanceof ApiError && (err.code === 'FILE_IN_USE' || err.status === 409)
        ? 'Fișierul este folosit într-un clip și nu poate fi șters.'
        : humanError(err);
      toast.push({ kind: 'err', title: 'Nu se poate șterge', body: msg });
    },
  });

  async function handleFiles(list: FileList | null) {
    if (!list || !list.length) return;
    setUploading(true);
    try {
      for (const f of Array.from(list)) {
        await uploadFile(f);
      }
      invalidate([qk.files()]);
      toast.push({ kind: 'ok', title: 'Fișiere încărcate' });
    } catch (err) {
      toast.push({ kind: 'err', title: 'Încărcare eșuată', body: humanError(err) });
    } finally {
      setUploading(false);
    }
  }

  const all = files.data?.items ?? [];
  const items = all.filter((f) => (filter === 'toate' ? true : f.kind === filter));

  return (
    <div>
      <PageHeader
        title="Galerie fișiere"
        subtitle="Orice fișier încărcat oriunde ajunge aici. Îl refolosești în orice selector din platformă."
        actions={
          <Segmented<Filter>
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'toate', label: 'Toate' },
              { value: 'image', label: 'Imagini' },
              { value: 'video', label: 'Video' },
              { value: 'audio', label: 'Audio' },
            ]}
          />
        }
      />

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className="card"
        data-testid="dropzone"
        style={{ padding: 28, border: `1.5px dashed ${drag ? 'var(--accent)' : 'var(--line)'}`, textAlign: 'center', cursor: 'pointer', marginBottom: 20 }}
      >
        <input ref={inputRef} type="file" multiple hidden onChange={(e) => handleFiles(e.target.files)} />
        <p style={{ margin: 0, fontWeight: 800 }}>{uploading ? 'Se încarcă…' : 'Trage fișierele aici sau apasă ca să alegi'}</p>
        <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--muted)' }}>JPG, PNG, MP4, MP3 — până la 50 MB.</p>
      </div>

      {files.isLoading ? (
        <div className="grid-auto">{[0, 1, 2, 3].map((i) => <Skeleton key={i} height={160} />)}</div>
      ) : items.length === 0 ? (
        <EmptyState illustration="🗂️" title="Galeria e goală" body="Încarcă imagini sau clipuri — le refolosești în orice clip." />
      ) : (
        <div className="grid-auto" data-testid="gallery-grid">
          {items.map((f) => (
            <div key={f.id} className="card" style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ aspectRatio: '1/1', borderRadius: 10, overflow: 'hidden', background: 'var(--bg2)', display: 'grid', placeItems: 'center' }}>
                {f.kind === 'image' ? <MediaImage fileId={f.id} /> : <span style={{ fontSize: 26 }}>{f.kind === 'video' ? '🎬' : f.kind === 'audio' ? '🎵' : '📄'}</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                  {f.originalName || f.kind}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {f.usedCount > 0 ? (
                  <span className="pill" style={{ background: 'var(--accent-soft)', color: 'var(--accent-text)' }}>
                    folosit în {f.usedCount} {f.usedCount === 1 ? 'clip' : 'clipuri'}
                  </span>
                ) : (
                  <span style={{ fontSize: 11, color: 'var(--muted)' }}>{formatNumber(Math.round(f.size / 1024))} KB</span>
                )}
                <button className="btn btn-ghost btn-sm" onClick={() => deleteMut.mutate(f.id)} style={{ marginLeft: 'auto', color: 'var(--err)', padding: '5px 9px' }} aria-label="Șterge">
                  Șterge
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
